import {CloseableContent, Modal, Notification, STATUS_TYPES} from 'dyl-components';
import React, {useContext, useState} from 'react';
import {Dropdown, Button, Popup, Header, Ref, Dimmer, Loader} from 'semantic-ui-react';

import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import './ConversionControls.scss';
import accountActions from "../../actions/account";
import LostForm from "shared/forms/LostForm";
import { StringUtils } from "utils";
import { QuoteBuilderContext } from 'shared/context/QuoteBuilderProvider';
import OutstandingInvoicesForm from 'shared/forms/OutstandingInvoicesForm';
import BulkActionsProvider from 'shared/context/BulkActionsProvider';
import subscriptionActions from "actions/subscription";
import ConvertAccountToCustomerForm from 'shared/forms/ConvertAccountToCustomerForm';
import { USER_PERMISSIONS } from 'dyl-components';
import RestrictedView from 'shared/RestrictedView';
import LinkContactsForm from 'shared/forms/LinkContactsForm';

const ConversionControls = () => {
    const { account_id } = useParams();
    const { customer_information, primary_contact_id, account_name, isDyl, assignees } = useSelector(state => ({
        customer_information: state.account.account?.customer,
        primary_contact_id: state.account.account?.primary_contact?.id,
        account_name: state.account.account?.name,
        isDyl: state.auth.dyl_employee,
        assignees: state.account.account?.assignees
    }));
    
    const modalRef = React.createRef();

    const [conversionModalOpen, setConversionModalOpen] = useState('');
    const [isConversionFormModified, setIsConversionFormModified] = useState(false);
    const [isLinkContactsModalOpen, setIsLinkContactsModalOpen] = useState(false);
    const { customer_status, fund_type } = customer_information;

    const onClose = () => {
        setConversionModalOpen('');
        setIsConversionFormModified(false);
    }
    
    const dispatch = useDispatch();
    
    const refresh = () => {
        dispatch(accountActions.readAccount(account_id));
    }
    
    const getPopupContent = () => {
        switch (conversionModalOpen) {
            case 'customer':
                return (
                    <React.Fragment>
                        <Header color='primary'>
                            Convert <Popup trigger={<span className='Convert__account-name'>{account_name}</span>} content={account_name} inverted /> to Customer
                        </Header>

                        <ConvertAccountToCustomerForm 
                            onClose={onClose}
                            size={'small'}
                            onModify={(isModified) => {setIsConversionFormModified(isModified)}}
                            refresh={refresh}
                            owner_id={primary_contact_id}
                            existing_account_id={account_id}
                        />
                    </React.Fragment>
                )
            case 'pause':
                return (
                    <React.Fragment>
                        <div className={"Account__lost-form"}>
                            <Header color='primary'>Pause Customer</Header>
                            <LostForm
                                account_id={account_id}
                                onClose={onClose}
                                type={'paused'}
                                account_stage={'customer'}
                                refresh={refresh}
                            />
                        </div>
                    </React.Fragment>
                )
            case 'cancel':
                return (
                    <React.Fragment>
                        <div className={"Account__lost-form"}>
                            <Header color='primary'>Cancel Customer</Header>
                            <LostForm
                                account_id={account_id}
                                onClose={onClose}
                                type={'canceled'}
                                account_stage={'customer'}
                                refresh={refresh}
                            />
                        </div>
                    </React.Fragment>
                )
            default:
                return null;
        }
    }

    function getActionText() {
        if (customer_status) {
            return StringUtils.capitalize(customer_status);
        }
        return 'Convert';
    }
    
    function getStatusClass() {
        if (customer_status) {
            if (customer_status === "active") {
                return "CustomerConversion--active"
            }
            return `CustomerConversion--${customer_status}`
        }
        return 'CustomerConversion--convert'
    }

    const { onOpenAddProducts } = useContext(QuoteBuilderContext);
    const [isSettlingOutstandingInvoices, setIsSettlingOutstandingInvoices] = useState(false);

    const isPaused = customer_status === 'paused';

    const reactivateCustomer = async () => {
        try {
            await dispatch(
                accountActions.reactivateCustomer(account_id, {
                    fund_type
                })
            )
            Notification.alert("Successfully reactivated customer", STATUS_TYPES.SUCCESS);
            refresh();
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to reactivate customer", STATUS_TYPES.ERROR);
        }
    }

    const isReactivatingCustomer = useSelector(state => state.account.customerBeingReactivated);

    return (
        <React.Fragment>
            <Popup
                content={(
                    <CloseableContent onClose={onClose}>
                        {getPopupContent()}
                    </CloseableContent>
                )}
                open={conversionModalOpen && conversionModalOpen !== 'reactivate'}
                position='bottom center'
                context={modalRef}
                closeOnDocumentClick={!isConversionFormModified}
                onClose={() => {
                    if (conversionModalOpen !== "pause" && conversionModalOpen !== "cancel") {
                        onClose();
                    }
                }}
                style={{zIndex:5}}
            />
            <Ref innerRef={modalRef}>
                <Dropdown as={Button} size='tiny' text={getActionText()} className={`CustomerConversion ${getStatusClass()}`}>
                    <Dropdown.Menu>
                        {customer_status === undefined && (
                            <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]} >
                                <Dropdown.Item onClick={() => {
                                    setConversionModalOpen('customer')
                                }}>
                                    Convert to Customer
                                </Dropdown.Item>
                            </RestrictedView>
                        )}
                        {(isPaused || customer_status === 'canceled') && (
                            <Dropdown.Item onClick={() => {
                                if (isPaused) {
                                    if (isDyl) {
                                        setIsSettlingOutstandingInvoices(true);
                                    } else {
                                        reactivateCustomer();
                                    }
                                } else {
                                    if (primary_contact_id) {
                                        onOpenAddProducts({
                                            isUpsell: true
                                        });
                                    } else {
                                        setIsLinkContactsModalOpen(true);
                                    }
                                }
                            }}>
                                {fund_type === 'trial' ? 'Upgrade' : 'Reactivate'}
                            </Dropdown.Item>
                        )}
                        {!isDyl && customer_status === 'active' && [
                            <Dropdown.Item onClick={() => {
                                setConversionModalOpen('pause')
                            }}>
                                Pause Customer
                            </Dropdown.Item>
                        ]}
                        {(customer_status === 'active' || isPaused) && (
                            <Dropdown.Item onClick={() => {
                                setConversionModalOpen('cancel')
                            }}>
                                Cancel Customer
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </Ref>
            <BulkActionsProvider>
                <OutstandingInvoicesForm 
                    isOpen={isSettlingOutstandingInvoices}
                    onClose={() => setIsSettlingOutstandingInvoices(false)}
                    customer_id={account_id}
                    formTitle={`Reactivate ${account_name}`}
                    refresh={() => {
                        dispatch(subscriptionActions.getAllProducts(account_id));
                        dispatch(subscriptionActions.getNextInvoice(account_id));
                        dispatch(accountActions.readAccount(account_id));
                    }}
                />
            </BulkActionsProvider>
            <Dimmer page active={isReactivatingCustomer}><Loader active={isReactivatingCustomer} /></Dimmer>

            <Modal open={isLinkContactsModalOpen} onClose={()=>{
                setIsLinkContactsModalOpen(false);
            }}>
                <LinkContactsForm
                    account_id={account_id}
                    onClose={()=>{
                        setIsLinkContactsModalOpen(false);
                        onOpenAddProducts({
                            isUpsell: true
                        });
                    }}
                    isArchived
                />
            </Modal> 
        </React.Fragment>
    );
};

export default ConversionControls;

